import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Container,
  Group,
  Menu,
  Paper,
  Select,
  Stack,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconDotsVertical,
  IconEdit,
  IconKey,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUserOff,
  IconUserCheck,
} from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import {
  useAdminUsersList,
  useDeactivateUser,
  useReactivateUser,
  useSoftDeleteUser,
} from '../../queries/admin-users';
import { UserFormDrawer } from './UserFormDrawer';
import { ResetPasswordDrawer } from './ResetPasswordDrawer';
import { ApiError } from '../../lib/api-error';
import { useAuthStore } from '../../auth/auth-store';
import type { SystemRole, User } from '../../types/api';

type StatusFilter = 'active' | 'inactive' | 'all';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
  { value: 'all', label: 'Both' },
];
const ROLE_OPTIONS: { value: SystemRole | ''; label: string }[] = [
  { value: '', label: 'All roles' },
  { value: 'user', label: 'user' },
  { value: 'admin', label: 'admin' },
  { value: 'superadmin', label: 'superadmin' },
];

export function AdminUsersPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [role, setRole] = useState<SystemRole | ''>('');

  const filters = useMemo(
    () => ({
      q: q || undefined,
      is_active: status === 'all' ? undefined : status === 'active',
      system_role: role || undefined,
    }),
    [q, status, role],
  );

  const { data, isLoading, isError, error } = useAdminUsersList(filters);
  const users = data?.users ?? [];
  const me = useAuthStore((s) => s.user);

  const [editUser, setEditUser] = useState<User | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  const deactivateM = useDeactivateUser();
  const reactivateM = useReactivateUser();
  const deleteM = useSoftDeleteUser();

  function openCreate() {
    setEditUser(null);
    setDrawerOpen(true);
  }

  function openEdit(u: User) {
    setEditUser(u);
    setDrawerOpen(true);
  }

  function openReset(u: User) {
    setResetUser(u);
    setResetOpen(true);
  }

  async function runAction(
    u: User,
    label: string,
    action: () => Promise<unknown>,
    confirmText?: string,
  ) {
    const go = async () => {
      try {
        await action();
        notifications.show({ title: label, message: u.username, color: 'pine' });
      } catch (err) {
        notifications.show({
          title: `${label} failed`,
          message: err instanceof ApiError ? err.message : 'Unexpected error',
          color: 'red',
        });
      }
    };
    if (confirmText) {
      modals.openConfirmModal({
        title: `${label}?`,
        children: <span>{confirmText}</span>,
        labels: { confirm: label, cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => void go(),
      });
    } else {
      await go();
    }
  }

  return (
    <Container size="xl">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end">
          <Stack gap={0}>
            <Title order={2}>Users</Title>
            {data && (
              <Badge variant="light" color="pine" size="sm">
                {data.total} total
              </Badge>
            )}
          </Stack>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            New user
          </Button>
        </Group>

        <Paper withBorder p="sm" radius="md">
          <Group gap="sm" wrap="wrap">
            <TextInput
              placeholder="Search by username or email"
              leftSection={<IconSearch size={14} />}
              value={q}
              onChange={(e) => setQ(e.currentTarget.value)}
              w={320}
            />
            <Select
              data={STATUS_OPTIONS}
              value={status}
              onChange={(v) => setStatus((v ?? 'active') as StatusFilter)}
              w={150}
            />
            <Select
              data={ROLE_OPTIONS}
              value={role}
              onChange={(v) => setRole((v ?? '') as SystemRole | '')}
              w={170}
            />
          </Group>
        </Paper>

        <Paper withBorder radius="md">
          <DataTable<User>
            records={users}
            fetching={isLoading}
            minHeight={200}
            noRecordsText={
              isError ? `Failed to load: ${(error as Error).message}` : 'No users found'
            }
            highlightOnHover
            columns={[
              { accessor: 'username', title: 'Username' },
              { accessor: 'email', title: 'Email' },
              {
                accessor: 'system_role',
                title: 'Role',
                width: 120,
                render: (u) => (
                  <Badge
                    variant="light"
                    color={
                      u.system_role === 'superadmin'
                        ? 'grape'
                        : u.system_role === 'admin'
                          ? 'blue'
                          : 'gray'
                    }
                  >
                    {u.system_role}
                  </Badge>
                ),
              },
              {
                accessor: 'is_active',
                title: 'Status',
                width: 120,
                render: (u) => (
                  <Group gap={4}>
                    <Badge color={u.is_active ? 'pine' : 'gray'} variant="light">
                      {u.is_active ? 'active' : 'inactive'}
                    </Badge>
                    {u.must_change_password && (
                      <Badge color="yellow" variant="light">
                        pending
                      </Badge>
                    )}
                  </Group>
                ),
              },
              {
                accessor: 'last_login_at',
                title: 'Last sign-in',
                width: 170,
                render: (u) =>
                  u.last_login_at ? dayjs(u.last_login_at).format('YYYY-MM-DD HH:mm') : '—',
              },
              {
                accessor: 'actions',
                title: '',
                width: 56,
                render: (u) => {
                  const isSelf = me?.id === u.id;
                  return (
                    <Group gap={2} justify="flex-end">
                      <Tooltip label="Edit">
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(u)}>
                          <IconEdit size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Menu position="bottom-end" withArrow>
                        <Menu.Target>
                          <ActionIcon size="sm" variant="subtle">
                            <IconDotsVertical size={14} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconKey size={14} />}
                            disabled={isSelf}
                            onClick={() => openReset(u)}
                          >
                            Reset password
                          </Menu.Item>
                          {u.is_active ? (
                            <Menu.Item
                              color="orange"
                              leftSection={<IconUserOff size={14} />}
                              disabled={isSelf}
                              onClick={() =>
                                runAction(
                                  u,
                                  'Deactivated',
                                  () => deactivateM.mutateAsync(u.id),
                                  `Deactivate ${u.username}? They won't be able to sign in.`,
                                )
                              }
                            >
                              Deactivate
                            </Menu.Item>
                          ) : (
                            <Menu.Item
                              color="pine"
                              leftSection={<IconUserCheck size={14} />}
                              onClick={() =>
                                runAction(u, 'Reactivated', () => reactivateM.mutateAsync(u.id))
                              }
                            >
                              Reactivate
                            </Menu.Item>
                          )}
                          <Menu.Divider />
                          <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            disabled={isSelf}
                            onClick={() =>
                              runAction(
                                u,
                                'Deleted',
                                () => deleteM.mutateAsync(u.id),
                                `Soft-delete ${u.username}? Account becomes invisible in lists. History is retained.`,
                              )
                            }
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  );
                },
              },
            ]}
          />
        </Paper>

        <UserFormDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} user={editUser} />
        <ResetPasswordDrawer
          opened={resetOpen}
          onClose={() => setResetOpen(false)}
          user={resetUser}
        />
      </Stack>
    </Container>
  );
}
